import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({ email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d6knpl94a3uojp5ffvj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ email: formData.email }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ email: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/0acaf1d9-c15d-42a1-b6db-d2cf3902df9b.png" 
                alt="STATIST Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-semibold text-gray-900 tracking-tight">STATIST, d.o.o.</span>
            </div>
            <div className="flex items-center gap-8">
              <a href="#home" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Home</a>
              <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">About</a>
              <a href="#products" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Products</a>
              <a href="#features" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Features</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen pt-20 overflow-hidden bg-gradient-to-br from-slate-800 via-purple-900 to-indigo-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            {/* Left Content */}
            <div className="lg:col-span-2 pt-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm mb-8">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-apple-fill text-white text-lg"></i>
                </div>
                <span className="text-white text-sm font-medium">iOS Development Experts</span>
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-white leading-tight mb-8">
                Modern iOS<br />
                Applications for<br />
                Enhanced Security
              </h1>
            </div>

            {/* Right Content */}
            <div className="lg:col-span-3 pt-12">
              <p className="text-lg text-white/80 leading-relaxed max-w-lg mb-12">
                We are a team of experts developing cutting-edge applications for the iOS platform. Our products, including VPN and Cleaner solutions, are meticulously designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
          </div>

          {/* Bottom Image Strip */}
          <div className="mt-16">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/aaac76a1349bc0051294fbaf5d1aaf4b.jpg" 
                alt="iOS Applications Showcase" 
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-16">
            <h2 className="text-5xl font-bold text-gray-900">About STATIST</h2>
            <p className="text-base text-gray-600 max-w-md">
              Dedicated to creating innovative iOS solutions that prioritize user security and device performance
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Card One - Wider */}
            <div className="lg:col-span-5 bg-gray-50 rounded-2xl shadow-lg overflow-hidden">
              <div className="h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20workspace%20with%20iOS%20development%20team%20working%20on%20MacBooks%20and%20iPhones%2C%20clean%20minimalist%20office%20environment%20with%20natural%20lighting%2C%20professional%20software%20developers%20coding%20mobile%20applications%2C%20contemporary%20tech%20company%20atmosphere%2C%20bright%20and%20airy%20setting%20with%20plants%20and%20modern%20furniture&width=600&height=400&seq=about-team-workspace&orientation=landscape" 
                  alt="Expert Team" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Team</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our talented developers bring years of iOS expertise, combining technical excellence with creative innovation to deliver applications that exceed expectations and set new standards in mobile security.
                </p>
              </div>
            </div>

            {/* Card Two */}
            <div className="lg:col-span-3 bg-gray-50 rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-6">VPN Solutions</h3>
                <div className="h-72 w-full flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20iPhone%20displaying%20elegant%20VPN%20application%20interface%20with%20secure%20connection%20visualization%2C%20clean%20minimalist%20UI%20design%20with%20blue%20and%20purple%20gradients%2C%20shield%20icon%20and%20encryption%20graphics%2C%20professional%20mobile%20app%20mockup%20on%20simple%20light%20background%2C%20premium%20iOS%20app%20design%20showcase&width=400&height=500&seq=product-vpn-interface&orientation=portrait" 
                    alt="VPN Solutions" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Card Three */}
            <div className="lg:col-span-4 bg-gray-50 rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Cleaner Technology</h3>
                <div className="h-72 w-full flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20sophisticated%20cleaner%20application%20interface%20with%20storage%20optimization%20graphics%2C%20modern%20minimalist%20UI%20with%20circular%20progress%20indicators%20and%20clean%20design%20elements%2C%20teal%20and%20white%20color%20scheme%2C%20professional%20iOS%20utility%20app%20mockup%20on%20simple%20light%20background%2C%20premium%20mobile%20app%20showcase&width=500&height=500&seq=product-cleaner-interface&orientation=squarish" 
                    alt="Cleaner Technology" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">Our Products</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Product Card 1 - Featured */}
            <div className="bg-white rounded-2xl shadow-xl p-10 relative">
              <div className="absolute top-6 left-6 bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Featured
              </div>
              <div className="mt-8 mb-8 flex justify-center">
                <div className="w-48 h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Premium%20iPhone%20mockup%20displaying%20VPN%20Pro%20application%20with%20sophisticated%20dark%20interface%2C%20secure%20connection%20status%20with%20glowing%20shield%20icon%2C%20elegant%20gradient%20background%20in%20blue%20and%20purple%20tones%2C%20professional%20mobile%20security%20app%20design%20on%20simple%20clean%20background%2C%20high-end%20iOS%20app%20presentation&width=400&height=550&seq=product-vpn-pro-featured&orientation=portrait" 
                    alt="VPN Pro" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">VPN Pro</h3>
                <p className="text-gray-600 mb-4">Premium security and privacy protection for your iOS device with military-grade encryption</p>
                <p className="text-lg font-semibold text-gray-700">$4.99/month</p>
              </div>
            </div>

            {/* Product Card 2 */}
            <div className="bg-white rounded-2xl shadow-xl p-10">
              <div className="mb-8 flex justify-center">
                <div className="w-48 h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20Cleaner%20Plus%20application%20interface%20with%20storage%20analysis%20visualization%2C%20colorful%20category%20breakdown%20charts%20and%20optimization%20tools%2C%20modern%20clean%20UI%20design%20with%20teal%20accents%20on%20simple%20light%20background%2C%20professional%20iOS%20utility%20app%20mockup%2C%20premium%20mobile%20app%20showcase&width=400&height=550&seq=product-cleaner-plus&orientation=portrait" 
                    alt="Cleaner Plus" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Cleaner Plus</h3>
                <p className="text-gray-600 mb-4">Advanced cleaning tools to optimize storage and boost device performance instantly</p>
                <p className="text-lg font-semibold text-gray-700">$2.99/month</p>
              </div>
            </div>

            {/* Product Card 3 */}
            <div className="bg-white rounded-2xl shadow-xl p-10">
              <div className="mb-8 flex justify-center">
                <div className="w-48 h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20showing%20Security%20Suite%20application%20with%20comprehensive%20protection%20dashboard%2C%20multiple%20security%20features%20icons%20and%20status%20indicators%2C%20professional%20dark%20blue%20interface%20design%20on%20simple%20clean%20background%2C%20premium%20iOS%20security%20app%20mockup%2C%20modern%20mobile%20app%20presentation&width=400&height=550&seq=product-security-suite&orientation=portrait" 
                    alt="Security Suite" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Security Suite</h3>
                <p className="text-gray-600 mb-4">Complete security package combining VPN protection with advanced threat detection</p>
                <p className="text-lg font-semibold text-gray-700">$7.99/month</p>
              </div>
            </div>

            {/* Product Card 4 */}
            <div className="bg-white rounded-2xl shadow-xl p-10">
              <div className="mb-8 flex justify-center">
                <div className="w-48 h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20VPN%20Express%20application%20with%20fast%20connection%20interface%2C%20speed%20test%20results%20and%20server%20selection%20map%2C%20sleek%20modern%20UI%20with%20green%20and%20blue%20gradients%20on%20simple%20light%20background%2C%20professional%20mobile%20VPN%20app%20mockup%2C%20premium%20iOS%20app%20showcase&width=400&height=550&seq=product-vpn-express&orientation=portrait" 
                    alt="VPN Express" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">VPN Express</h3>
                <p className="text-gray-600 mb-4">Lightning-fast VPN service with global server network for seamless browsing</p>
                <p className="text-lg font-semibold text-gray-700">$3.99/month</p>
              </div>
            </div>

            {/* Product Card 5 */}
            <div className="bg-white rounded-2xl shadow-xl p-10">
              <div className="mb-8 flex justify-center">
                <div className="w-48 h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20showing%20Performance%20Optimizer%20application%20with%20system%20monitoring%20graphs%2C%20CPU%20and%20memory%20usage%20displays%2C%20modern%20minimalist%20interface%20with%20orange%20and%20blue%20accents%20on%20simple%20clean%20background%2C%20professional%20iOS%20utility%20app%20mockup%2C%20premium%20mobile%20app%20presentation&width=400&height=550&seq=product-performance-optimizer&orientation=portrait" 
                    alt="Performance Tools" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Performance Tools</h3>
                <p className="text-gray-600 mb-4">Comprehensive toolkit to monitor and enhance your device's speed and efficiency</p>
                <p className="text-lg font-semibold text-gray-700">$1.99/month</p>
              </div>
            </div>

            {/* Product Card 6 */}
            <div className="bg-white rounded-2xl shadow-xl p-10">
              <div className="mb-8 flex justify-center">
                <div className="w-48 h-64">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20Smart%20Cleaner%20application%20with%20intelligent%20storage%20management%20interface%2C%20AI-powered%20cleaning%20suggestions%20and%20duplicate%20finder%2C%20modern%20purple%20and%20white%20UI%20design%20on%20simple%20light%20background%2C%20professional%20iOS%20cleaner%20app%20mockup%2C%20premium%20mobile%20app%20showcase&width=400&height=550&seq=product-smart-cleaner&orientation=portrait" 
                    alt="Smart Cleaner" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Smart Cleaner</h3>
                <p className="text-gray-600 mb-4">Intelligent cleaning solution with AI-powered optimization for maximum efficiency</p>
                <p className="text-lg font-semibold text-gray-700">$3.49/month</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-6">
              Why Choose Us
            </div>
            <h2 className="text-6xl font-bold text-gray-900 leading-tight">
              Enhancing Mobile<br />
              Security & Performance
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {/* Feature Card 1 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-indigo-900 flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Military-Grade Encryption</h3>
                  <p className="text-sm text-gray-500">@security_standard</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our VPN solutions utilize AES-256 encryption, the same standard used by governments and security experts worldwide, ensuring your data remains completely private and secure from any potential threats.
              </p>
            </div>

            {/* Feature Card 2 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-indigo-900 flex-shrink-0">
                  <i className="ri-speed-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Lightning Performance</h3>
                  <p className="text-sm text-gray-500">@speed_optimization</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Experience blazing-fast connection speeds with our optimized server infrastructure. Our applications are engineered to deliver maximum performance without compromising on security or reliability.
              </p>
            </div>

            {/* Feature Card 3 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-indigo-900 flex-shrink-0">
                  <i className="ri-smartphone-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">iOS Native Design</h3>
                  <p className="text-sm text-gray-500">@platform_integration</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Built exclusively for iOS, our applications seamlessly integrate with Apple's ecosystem, providing an intuitive user experience that feels natural and familiar to every iPhone user.
              </p>
            </div>

            {/* Feature Card 4 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-indigo-900 flex-shrink-0">
                  <i className="ri-delete-bin-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Smart Cleaning</h3>
                  <p className="text-sm text-gray-500">@storage_management</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our intelligent cleaning algorithms identify and remove unnecessary files, duplicates, and cached data, freeing up valuable storage space while maintaining the integrity of your important files.
              </p>
            </div>

            {/* Feature Card 5 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-indigo-900 flex-shrink-0">
                  <i className="ri-global-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Global Server Network</h3>
                  <p className="text-sm text-gray-500">@worldwide_coverage</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Access content from anywhere with our extensive network of servers spanning multiple continents. Enjoy unrestricted browsing with consistently high speeds regardless of your location.
              </p>
            </div>

            {/* Feature Card 6 */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-indigo-900 flex-shrink-0">
                  <i className="ri-customer-service-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">24/7 Support</h3>
                  <p className="text-sm text-gray-500">@customer_care</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our dedicated support team is available around the clock to assist you with any questions or concerns. We're committed to ensuring you have the best possible experience with our products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-16 pb-8 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/0acaf1d9-c15d-42a1-b6db-d2cf3902df9b.png" 
                alt="STATIST Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-medium text-white">STATIST, d.o.o.</span>
            </div>
            <p className="text-gray-400">Modern iOS Development Solutions</p>
          </div>

          {/* Middle Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Column 1 */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Products</h4>
              <ul className="space-y-4">
                <li className="text-gray-400">VPN Pro</li>
                <li className="text-gray-400">Cleaner Plus</li>
                <li className="text-gray-400">Security Suite</li>
                <li className="text-gray-400">Performance Tools</li>
              </ul>
            </div>

            {/* Column 2 */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Company</h4>
              <ul className="space-y-4">
                <li className="text-gray-400">About Us</li>
                <li className="text-gray-400">Our Team</li>
                <li className="text-gray-400">Expertise</li>
                <li className="text-gray-400">Technology</li>
              </ul>
            </div>

            {/* Column 3 */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Support</h4>
              <ul className="space-y-4">
                <li className="text-gray-400">Documentation</li>
                <li className="text-gray-400">Resources</li>
                <li className="text-gray-400">Updates</li>
                <li className="text-gray-400">Guidelines</li>
              </ul>
            </div>

            {/* Column 4 */}
            <div className="text-right">
              <p className="text-white font-serif mb-2">© STATIST 2025</p>
              <p className="text-sm text-gray-400 mb-6">All rights reserved</p>
              <div className="flex justify-end gap-4 text-sm text-gray-400">
                <span className="underline cursor-pointer hover:text-white transition-colors">LinkedIn</span>
                <span className="underline cursor-pointer hover:text-white transition-colors">Twitter</span>
                <span className="underline cursor-pointer hover:text-white transition-colors">GitHub</span>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pt-12 border-t border-gray-700">
            {/* Left Visual */}
            <div className="lg:col-span-4">
              <div className="rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20MacBook%20and%20iPhone%20on%20clean%20desk%2C%20contemporary%20iOS%20development%20environment%20with%20natural%20lighting%2C%20professional%20tech%20company%20setting%20with%20plants%20and%20modern%20furniture%2C%20bright%20and%20sophisticated%20atmosphere%2C%20clean%20aesthetic%20with%20blue%20and%20purple%20accent%20lighting&width=500&height=300&seq=contact-office-environment&orientation=landscape" 
                  alt="Office Environment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Middle Copy */}
            <div className="lg:col-span-4 flex items-center">
              <h3 className="text-3xl font-serif text-white leading-relaxed">
                Building the future<br />
                of mobile<br />
                applications
              </h3>
            </div>

            {/* Right Subscribe & Contact */}
            <div className="lg:col-span-4">
              <div className="mb-8">
                <p className="text-sm text-gray-400 mb-4">
                  Stay updated with our<br />
                  latest developments
                </p>
                <form id="newsletter-form" onSubmit={handleSubmit} data-readdy-form className="flex gap-2 mb-2">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ email: e.target.value })}
                    required
                    className="flex-1 px-4 py-3 bg-transparent border border-gray-600 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Subscribe'}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </form>
                {submitStatus === 'success' && (
                  <p className="text-sm text-green-400">Successfully subscribed!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-sm text-red-400">Subscription failed. Please try again.</p>
                )}
              </div>

              {/* Contact Information */}
              <div className="space-y-3 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-medium">Address:</span>
                  <span>Do proge 8</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-building-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-medium">City:</span>
                  <span>Ljubljana-Sentvid</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-medium">Zip:</span>
                  <span>1210</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-global-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-medium">Country:</span>
                  <span>Slovenia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-mail-send-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-medium">Email:</span>
                  <a href="mailto:info@statistdoo.com" className="hover:text-white transition-colors cursor-pointer">info@statistdoo.com</a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-medium">Number:</span>
                  <a href="tel:+16605382857" className="hover:text-white transition-colors cursor-pointer">+1 6605382857</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}